import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import RootFractionInput, {
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";
import { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";

function gcdInt(a, b) {
  a = Math.abs(Math.trunc(a));
  b = Math.abs(Math.trunc(b));
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  if (a === 0 && b === 0) return 1;

  while (b) [a, b] = [b, a % b];
  return a === 0 ? 1 : Math.abs(a);
}

function simplifyFraction(numerator, denominator) {
  const d = gcdInt(numerator, denominator);
  let n = numerator / d;
  let den = denominator / d;
  if (den < 0) {
    n = -n;
    den = -den;
  }
  return { numerator: n, denominator: den };
}

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generateTask() {
  const xCandidates = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
  const yCandidates = [1, 2, 3, 4, 5, 6, 7, 8];

  let x, y;
  do {
    x = randomChoice(xCandidates);
    y = randomChoice(yCandidates);
  } while (x === 0);

  const expected = simplifyFraction(y, x);

  const data = {
    type: "FindTanFromPointTask",
    x,
    y,
    expected,
  };

  return {
    ...data,
    data, // ✅ dla quizu
  };
}

export function renderTask({ x, y }) {
  return (
    <div className="space-y-4" style={{ marginTop: "10px" }}>
      <div>
        Dany jest punkt <InlineMath math={`P = (${x}, ${y})`} />. Oblicz{" "}
        <InlineMath math={`\\tan\\, \\alpha`} />, gdzie{" "}
        <InlineMath math={`\\alpha`} /> to kąt pomiędzy odcinkiem{" "}
        <InlineMath math={`OP`} /> a dodatnią osią <InlineMath math={`x`} />.
      </div>
      <div style={{ height: 360 }}>
        <GraphViewer
          points={{
            O: {
              x: 0,
              y: 0,
              textPosition: x > 0 && y > 0 ? "top left" : "top right",
            },
            P: { x, y },
          }}
          segments={[{ p1: "O", p2: "P" }]}
          xRange={[-6, 6]}
          yRange={[-3, 9]}
          width={350}
          height={350}
        />
      </div>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\tan\\,\\alpha`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <InlineMath
      math={`\\displaystyle ${formatLatexPreview(raw, "\\tan\\,\\alpha")}`}
    />
  );
}

export function checkAnswer(task, userAnswer) {
  const expected = task.expected;
  const expectedValue = expected.numerator / expected.denominator;

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (parsed != null && isFinite(parsed)) {
    return Math.abs(expectedValue - parsed) < 0.01;
  }

  const sign = expected.numerator < 0 ? "-" : "";
  const absNum = Math.abs(expected.numerator);
  const numStr = absNum === 1 ? "1" : String(absNum);
  const expectedLatex =
    expected.denominator === 1
      ? `${sign}${numStr}`
      : `${sign}\\dfrac{${numStr}}{${expected.denominator}}`;

  const normalize = (str) =>
    String(str ?? "")
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "");

  return normalize(raw) === normalize(expectedLatex);
}

export function renderAnswer({ expected }) {
  const sign = expected.numerator < 0 ? "-" : "";
  const absNum = Math.abs(expected.numerator);
  const numStr = absNum === 1 ? "1" : String(absNum);

  return (
    <InlineMath
      math={`\\tan\\, \\alpha = ${
        expected.denominator === 1
          ? `${sign}${numStr}`
          : `${sign}\\dfrac{${numStr}}{${expected.denominator}}`
      }`}
    />
  );
}

export function renderExplanation(payload) {
  const t =
    payload && payload.data && payload.data.x != null ? payload.data : payload;

  const x = t?.x;
  const y = t?.y;
  const expected = t?.expected;

  if (x == null || y == null || !expected) {
    return <div>(brak danych do wyjaśnienia)</div>;
  }

  const sign = expected.numerator < 0 ? "-" : "";
  const absNum = Math.abs(expected.numerator);
  const numStr = absNum === 1 ? "" : String(absNum);

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru:
        <BlockMath math={`\\tan\\, \\alpha = \\dfrac{y}{x}`} />
      </div>
      <div>
        Podstawiamy:
        <BlockMath math={`\\tan\\, \\alpha = \\dfrac{${y}}{${x}}`} />
        <BlockMath
          math={`\\tan\\, \\alpha = ${
            expected.denominator === 1
              ? `${sign}${numStr || "1"}`
              : `${sign}\\dfrac{${numStr || "1"}}{${expected.denominator}}`
          }`}
        />
      </div>
    </div>
  );
}
